import { useMemo } from 'react';
import { Flatten, FormTrucker } from '@/types';
import { useTruckerAutoFillVerify } from '@/components/FormWrapper/hooks';
import { useAppContext } from '@/context';

type ValidateFn = (field?: keyof Flatten<FormTrucker>) => boolean;

type DisabledFieldProps = {
  field: keyof Flatten<FormTrucker>;
  isDisabled?: boolean | ValidateFn;
};

const isDisabledFn = ({ field, isDisabled }: DisabledFieldProps) => {
  if (typeof isDisabled === 'boolean') return isDisabled;

  return isDisabled?.(field);
};

export const useIsDisabledField = () => {
  const { state } = useAppContext();
  const truckerAutoFillVerify = useTruckerAutoFillVerify(state);
  const isResubmit = useMemo(() => Boolean(state.risk.backendResponse?.uuid), [state.risk.backendResponse]);
  const isValidTrucker = useMemo(() => {
    if (!state.truckerProfile.isLoaded) return false;
    const { isRegistered, truckerIsActive } = state.truckerProfile.data;
    return (isRegistered && truckerIsActive) || (!isRegistered && !truckerIsActive);
  }, [state.truckerProfile]);
  const isLoadedProfile = useMemo(
    () => state.truckerProfile.isLoaded || isResubmit,
    [state.truckerProfile.isLoaded, isResubmit],
  );

  const isDisabledField = ({ field, isDisabled }: DisabledFieldProps) => {
    if (isDisabled !== undefined) return isDisabledFn({ field, isDisabled });
    if (isResubmit) return false;
    if (!isValidTrucker) return true;

    return !isLoadedProfile || truckerAutoFillVerify(field);
  };

  return { isDisabledField, isResubmit, isLoadedProfile };
};
